import { Show } from 'solid-js'

import { useI18n } from '../../i18n'
import {
   celebratingHabitIds,
   getHabitCompletionLabel,
   getHabitDueLabel,
   getHabitScheduleLabel,
   habits,
   isHabitOverdue,
   type Habit,
} from '../../state/habitsStore'
import EditIconButton from '../ui/EditIconButton'
import HabitCompletionBorder from './HabitCompletionBorder'

export type HabitPlacement = 'due' | 'undue'

type HabitTaskCardProps = {
   habit: Habit
   placement: HabitPlacement
   currentTime: number
   canComplete: boolean
   onComplete: (habit: Habit, placement: HabitPlacement) => void
   onEdit: (habit: Habit) => void
}

/**
 * Renders a single habit task card with complete/edit actions.
 *
 * @param props - The card props.
 * @returns The card element.
 */
const HabitTaskCard = (props: HabitTaskCardProps) => {
   const t = useI18n()
   const dueLabel = () => getHabitDueLabel(props.habit, props.currentTime)
   const scheduleLabel = () => getHabitScheduleLabel(props.habit)
   const completionLabel = () => getHabitCompletionLabel(props.habit, props.currentTime)
   const isOverdue = () => isHabitOverdue(props.habit, props.currentTime)
   const isCelebrating = () => celebratingHabitIds().includes(props.habit.id)
   const completionButtonLabel = () =>
      props.canComplete ? t('Done') : t('Available on due date')
   /**
    * Returns names of unpaused habits whose completion also completes this one.
    *
    * @returns The covering habit names.
    */
   const coveringHabitNames = () =>
      habits()
         .filter(
            habit =>
               habit.id !== props.habit.id &&
               !habit.paused &&
               habit.alsoCompletes.includes(props.habit.id),
         )
         .map(habit => habit.name)

   return (
      <article
         class="habit-task rounded-lg border border-edge-soft bg-surface-sunken/35 p-3 transition"
         data-due={props.placement === 'due' ? 'true' : 'false'}
         data-placement={props.placement === 'undue' ? 'undue' : undefined}
         data-paused={props.habit.paused ? 'true' : 'false'}
         data-overdue={isOverdue() ? 'true' : 'false'}
         data-celebrating={isCelebrating() ? 'true' : 'false'}
      >
         <HabitCompletionBorder flaming={isOverdue() && !isCelebrating()} />
         <div class="habit-task-content grid grid-cols-[minmax(0,1fr)_2.5rem] gap-3 sm:items-center">
            <div class="order-2 flex flex-col items-center gap-2">
               <button
                  type="button"
                  class="habit-icon-button bg-emerald-500 text-ink-inverse hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-surface-control disabled:text-ink-faint"
                  title={completionButtonLabel()}
                  aria-label={completionButtonLabel()}
                  disabled={!props.canComplete}
                  onClick={() => props.onComplete(props.habit, props.placement)}
               >
                  <svg aria-hidden="true" viewBox="0 0 24 24" class="h-5 w-5">
                     <path
                        d="M20 6 9 17l-5-5"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                     />
                  </svg>
               </button>
               <EditIconButton onClick={() => props.onEdit(props.habit)} />
            </div>
            <div class="order-1 min-w-0">
               <h3 class="truncate text-base font-semibold text-ink">
                  {props.habit.name}
               </h3>
               <p class="text-sm text-ink-muted">
                  {props.habit.paused ?
                     t('Paused')
                  :  t(dueLabel().key, {
                        count: dueLabel().count,
                        time: dueLabel().time ?? '',
                     })
                  }
               </p>
               <p class="mt-1 text-xs text-ink-faint">
                  {t(scheduleLabel().key, { count: scheduleLabel().count })}
               </p>
               <p class="mt-1 text-xs text-ink-faint">
                  {t(completionLabel().key, { count: completionLabel().count })}
               </p>
               <Show when={coveringHabitNames().length > 0}>
                  <p class="mt-1 text-xs text-emerald-200/70">
                     {t('Or instead: {names}', {
                        names: coveringHabitNames().join(', '),
                     })}
                  </p>
               </Show>
            </div>
         </div>
      </article>
   )
}

export default HabitTaskCard
